import React from 'react';
import Text from '@/components/Text';
import { SlideProps } from './slideTypes';
import { BCG_QUADRANTS, SLIDE_COMPONENT_TEXT_SIZE, SLIDE_COMPONENT_TITLE_SIZE } from './slideConstants';

export function BcgMatrixSlide({ slide }: SlideProps) {
  // BCG Matrix - 2x2 quadrant chart
  const matrixData = Array.isArray(slide.data) 
    ? slide.data 
    : Array.isArray(slide.data?.data) 
      ? slide.data.data 
      : [];

  const validMatrixData = matrixData.filter(
    (item: any) => item && item.title && (item.quadrant || (item.growth && item.share) || item.value)
  );

  const getQuadrant = (item: any, idx: number): string => {
    if (item.quadrant) return item.quadrant.toLowerCase();
    
    if (item.growth && item.share) {
      const growth = item.growth.toLowerCase();
      const share = item.share.toLowerCase();
      const isHighGrowth = growth === 'high' || growth === 'moderate' || growth === 'medium';
      const isHighShare = share === 'high' || share === 'moderate' || share === 'medium';
      
      if (isHighGrowth) {
        if (isHighShare) return 'stars';
        return 'questionMarks';
      }
      if (isHighShare) return 'cashCows';
      return 'dogs';
    }
    
    if (item.value) {
      const value = item.value.toLowerCase();
      const isHighShare = idx % 2 === 0;
      if (value === 'high') return isHighShare ? 'stars' : 'questionMarks';
      return isHighShare ? 'cashCows' : 'dogs';
    }
    
    return 'dogs';
  };

  const quadrantItems: Record<string, any[]> = {
    stars: [],
    questionMarks: [],
    cashCows: [],
    dogs: [],
  };

  validMatrixData.forEach((item: any, idx: number) => {
    const q = getQuadrant(item, idx);
    if (quadrantItems[q]) quadrantItems[q].push(item);
  });

  const renderQuadrant = (key: string, position: 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight') => {
    const quadrant = BCG_QUADRANTS[key as keyof typeof BCG_QUADRANTS];
    const items = quadrantItems[key] || [];
    
    const borderClasses = {
      topLeft: 'rounded-tl-2xl rounded-tr-2xl rounded-bl-2xl rounded-br-none',
      topRight: 'rounded-tl-2xl rounded-tr-2xl rounded-bl-none rounded-br-2xl',
      bottomLeft: 'rounded-tl-2xl rounded-tr-none rounded-bl-2xl rounded-br-2xl',
      bottomRight: 'rounded-tl-none rounded-tr-2xl rounded-bl-2xl rounded-br-2xl',
    };

  return (
      <div className={`flex-1 p-1.5 md:p-4 min-h-[85px] md:min-h-[140px] flex flex-col gap-1.5 md:gap-3 bg-white/10 backdrop-blur-sm border border-white/5 ${borderClasses[position]}`}>
        <div className="flex justify-between items-start gap-1 md:gap-2">
          <Text size="xs" className={`font-normal ${quadrant.color} text-[9px] md:text-sm line-clamp-1`}>
            {quadrant.label}
          </Text>
          <div className="w-5 h-5 md:w-10 md:h-10 rounded-full flex items-center justify-center flex-none" style={{ backgroundColor: quadrant.iconBg }}>
            <img 
              src={quadrant.icon} 
              alt={quadrant.label}
              className="w-3 h-3 md:w-6 md:h-6 object-contain"
            />
          </div>
        </div>
        
        <div className="flex flex-col gap-1 md:gap-2">
          {items.slice(0, 2).map((item: any, idx: number) => (
            <div key={idx} className="py-1 px-1.5 md:py-2 md:px-3 rounded-md md:rounded-lg bg-white/5 border border-white/5">
              <Text size={SLIDE_COMPONENT_TEXT_SIZE} className="text-white font-normal leading-tight text-left text-[9px] md:text-md line-clamp-1">
                {item.title}
              </Text>
            </div>
          ))}
          {items.length > 2 && (
            <Text size="xs" className="text-white/40 italic ml-1 text-[9px] md:text-xs">
              +{items.length - 2} more
            </Text>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-3 md:gap-6 w-full px-2 md:px-6 py-4 md:py-8 items-start justify-center overflow-y-auto max-h-full">
      <Text size={SLIDE_COMPONENT_TITLE_SIZE} className="text-white font-normal mb-1 md:mb-2 text-left uppercase tracking-widest text-sm md:text-lg px-2 md:px-0">BCG Matrix</Text>
      
      <div className="flex w-full gap-1 md:gap-4 overflow-x-auto pb-2 max-w-full">
        {/* Y-Axis Label */}
        <div className="flex flex-col justify-between py-8 md:py-16 relative w-5 md:w-8 h-[180px] md:h-[280px] flex-none">
          <Text size="xs" className="text-white/40 font-normal uppercase tracking-widest absolute -rotate-90 whitespace-nowrap top-1/4 -left-6 md:-left-10 text-[7px] md:text-xs">
            High Growth
          </Text>
          <Text size="xs" className="text-white/40 font-normal uppercase tracking-widest absolute -rotate-90 whitespace-nowrap top-3/4 -left-6 md:-left-10 text-[7px] md:text-xs">
            Low Growth
          </Text>
        </div>

        <div className="flex flex-col gap-1 flex-1 min-w-0">
          {/* Top Row */}
          <div className="flex gap-1">
            {renderQuadrant('stars', 'topLeft')}
            {renderQuadrant('questionMarks', 'topRight')}
          </div>
          
          {/* Bottom Row */}
          <div className="flex gap-1">
            {renderQuadrant('cashCows', 'bottomLeft')}
            {renderQuadrant('dogs', 'bottomRight')}
          </div>

          {/* X-Axis Label */}
          <div className="flex justify-around mt-1 md:mt-4 w-full">
            <Text size="xs" className="text-white/40 font-normal uppercase tracking-widest text-[7px] md:text-xs">
              High Share
            </Text>
            <Text size="xs" className="text-white/40 font-normal uppercase tracking-widest text-[7px] md:text-xs">
              Low Share
            </Text>
          </div>
        </div>
      </div>
    </div>
  );
}
